import { useEffect, useRef, useState } from 'react';
import { cloneElement, isValidElement } from "react";
import styles from './Modal.module.css';
import { IoClose } from "react-icons/io5";

export default function Modal({
    isOpen,
    onClose,
    children,
    title,
    icon,
    closeIcon,
    actionButtons,
    errorMessage,
    shrink = false,
    tall = false,
    wide = false,
    contentClassName = styles['modal-container'],
    iconClassName = styles['modal-icon'],
    titleClassName = styles['modal-title'],
    overlayClassName = styles['modal-overlay'],
    animate = true,
    animateIn = true,
    animateOut = true,
}) {
    const modalRef = useRef(null);
    const [isVisible, setIsVisible] = useState(isOpen);
    const [isClosing, setIsClosing] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setIsClosing(true);
            const timeoutId = window.setTimeout(() => {
                setIsVisible(false);
                setIsClosing(false);
            }, 180);

            return () => window.clearTimeout(timeoutId);
        }

        setIsVisible(true);
        setIsClosing(false);
    }, [isOpen]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (event.target instanceof Element && event.target.closest('[data-modal-safe-portal="true"]')) {
                return;
            }

            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose?.();
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen, onClose]);

    if (!isVisible) {
       return null;
    }

    const actionButtonColorClasses = {
        accent: styles['modal-action-button-accent'],
        dark: styles['modal-action-button-dark'],
        light: styles['modal-action-button-light'],
        danger: styles['modal-action-button-danger'],
    };

    const shrinkClass = shrink ? styles['modal-shrink'] : '';
    const tallClass = tall ? styles['modal-tall'] : '';
    const wideClass = wide ? styles['modal-wide'] : '';
    const renderedCloseIcon = isValidElement(closeIcon)
        ? cloneElement(closeIcon, {
            className: [closeIcon.props.className, styles['modal-close-icon']].filter(Boolean).join(' '),
            'aria-hidden': true,
        })
        : <IoClose className={styles['modal-close-icon']} aria-hidden="true" />;

    const renderedActionButtons = Array.isArray(actionButtons)
        ? actionButtons.map((button, index) => {
            if (!button) {
                return null;
            }

            const {
                key,
                text,
                label,
                icon: buttonIcon,
                children: buttonChildren,
                className,
                color = 'accent',
                type = 'button',
                ...buttonProps
            } = button;

            return (
                <button
                    key={key ?? text ?? label ?? index}
                    type={type}
                    className={[
                        styles['modal-action-button'],
                        actionButtonColorClasses[color] ?? actionButtonColorClasses.accent,
                        className,
                    ].filter(Boolean).join(' ')}
                    {...buttonProps}
                >
                    {buttonChildren ?? (
                        <>
                            <span className={styles['modal-action-button-label']}>{text ?? label}</span>
                            {buttonIcon && <span className={styles['modal-action-button-icon']}>{buttonIcon}</span>}
                        </>
                    )}
                </button>
            );
        })
        : actionButtons;

    const shouldAnimateIn = animate && animateIn;
    const shouldAnimateOut = animate && animateOut;
    const overlayAnimationClass = shouldAnimateIn || shouldAnimateOut
        ? (isClosing ? (shouldAnimateOut ? styles['modal-overlay-animate-reverse'] : '') : (shouldAnimateIn ? styles['modal-overlay-animate'] : ''))
        : '';
    const containerAnimationClass = shouldAnimateIn || shouldAnimateOut
        ? (isClosing ? (shouldAnimateOut ? styles['modal-container-animate-reverse'] : '') : (shouldAnimateIn ? styles['modal-container-animate'] : ''))
        : '';

    return (
        <div className={[
                overlayClassName, 
                overlayAnimationClass
            ].filter(Boolean).join(' ')}>

            <div className={[
                styles['modal-container'],
                shrinkClass,
                tallClass,
                wideClass,
                containerAnimationClass,
                ].filter(Boolean).join(' ')}>

                <div className={styles['modal-headerRow']}>
                    <div className={styles['modal-titleContainer']}>
                        <h1 className={titleClassName}>{title}</h1>
                        {icon && <span className={iconClassName}>{icon}</span>}
                    </div>
                    <button
                        type="button"
                        className={styles['modal-close-button']}
                        onClick={() => onClose?.()}
                        aria-label="Close modal"
                    >
                        {renderedCloseIcon}
                    </button>
                </div>
                <div className={[
                    styles['modal-content-container'],
                    contentClassName,
                ].filter(Boolean).join(' ')} ref={modalRef}>
                    <div className={styles['modal-content']}>
                        {children}
                    </div>
                    {renderedActionButtons && (
                        <div className={styles['modal-action-buttons']}>
                            {renderedActionButtons}
                        </div>
                    )}
                    {errorMessage && (
                        <div className={styles['modal-error-message']} role="alert">
                            {errorMessage}
                        </div>
                    )}
                </div>
            </div>

        </div>
    );
}   